<template>
  <!-- CD-7 boost details -->
  <div class="container mb-16">
    <div class="flex justify-between mb-4">
      <Button cls variant="ghost" size="icon" class="border rounded-lg" @click="goBack">
        <ArrowLeft class="h-6 w-6" />
      </Button>
      <img
        :src="BoostActiveIcon"
        v-if="isRewards"
        :class="isMobileOrIsTable ? 'h-[24px] w-[70px]' : 'h-[32px] w-[94px]'"
      />
    </div>
    <div class="flex justify-center" v-if="isLoading">
      <CustomSkeleton />
    </div>
    <OrderDetailsCard
      v-if="!isLoading"
      :merchant-name="order?.merchantName ?? ''"
      :reference="order?.reference ?? ''"
      :image-url="order?.imageUrl ?? ''"
      :progress="progress"
      :remaining-time="remainingTime"
      :show-overlay="showOverlay"
      :is-mobile="isMobileOrIsTable"
      :is-redeemed="isRedeemed"
      :is-cancelled="isCancelled"
      :hide-progress="isRedeemed"
      progress-bar-class="rounded-l-full"
      progress-margin-class="mt-[-2px]"
      :progress-text-class="progressTextClass"
    >
      <template #progress-icon>
        <img :src="TrophyIcon" class="relative top-[-8px] left-[-.5px]" />
      </template>

      <template #threshold-badge>
        <span
          v-if="isThresholdReached"
          class="text-[14px] text-success-700 bg-success-50 border-[1px] border-success-200 rounded-[6px] py-[3px] px-[6px] flex items-center gap-1"
        >
          <Check class="h-3 w-3" />
          Minimum savings threshold reached
        </span>
      </template>

      <template #desktop-badge>
        <Badge
          class="rounded-full ml-2 text-[10px] text-error-600 border-error-200 bg-error-50"
          variant="outline"
          v-if="isCancelled"
        >
          {{ order?.state }}
        </Badge>
      </template>

      <template #mobile-fields>
        <div v-if="!isRedeemed" class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Savings Goal</p>
            <p class="text-[18px] font-extraBold" :class="valueTextClass">{{ displayCurrency(orderTotal) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Current Savings</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(paidAmount) }}</p>
          </div>
        </div>

        <div v-if="!isRedeemed" class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Outstanding</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(orderBalance) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Boost Rewards</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(boostRewardAmount) }}</p>
          </div>
        </div>

        <div v-if="!isRedeemed" class="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Duration</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ duration }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Savings Threshold</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(savingsThreshold) }}</p>
          </div>
        </div>
      </template>

      <template #desktop-fields>
        <div v-if="!isRedeemed" class="grid grid-cols-3 gap-6">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Savings Goal</p>
            <p class="text-[22px] font-extrabold" :class="valueTextClass">{{ displayCurrency(orderTotal) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Current Savings</p>
            <p class="text-[20px] font-semibold" :class="valueTextClass">{{ displayCurrency(paidAmount) }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Outstanding</p>
            <p class="text-[20px] font-semibold" :class="valueTextClass">{{ displayCurrency(orderBalance) }}</p>
          </div>
        </div>

        <div v-if="!isRedeemed" class="grid grid-cols-3 gap-6">
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Boost Rewards</p>
            <p class="text-[18px] font-semibold" :class="isCancelled ? 'text-grey-light-300' : 'text-brand-500'">
              {{ displayCurrency(boostRewardAmount) }}
            </p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Duration</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ duration }}</p>
          </div>
          <div>
            <p class="text-[10px] text-grey-light-600 mb-1">Savings Threshold</p>
            <p class="text-[18px] font-semibold" :class="valueTextClass">{{ displayCurrency(savingsThreshold) }}</p>
          </div>
        </div>
      </template>

      <template #mobile-actions>
        <div class="flex flex-col gap-3 w-full">
          <div
            v-if="isCancelled"
            class="bg-error-50 text-error-700 hover:bg-error-50 w-full border-error-200 border-[1px] h-[40px] rounded-[9999px] flex justify-center items-center px-2"
          >
            Cancelled
          </div>
          <Button
            v-if="isThresholdReached && !isCancelled && !isCancelling && !isRedeemed"
            class="border border-brand-300 text-brand-700 bg-white hover:bg-white w-full"
            @click="handleRedeem"
            :disabled="isCancelled || isCompleted"
            >Redeem</Button
          >
          <Button
            v-if="!isRedeemed && !isCancelled && !isCancelling"
            variant="outline"
            class="border-error-200 text-error-700 hover:text-error-700 w-full"
            @click="cancelOrder"
            >Cancel</Button
          >
          <div v-if="isRedeemed" class="flex justify-center w-full">
            <div
              class="flex items-center justify-center gap-2 border-[1px] border-boost-500 text-boost-500 bg-boost-25 h-[40px] rounded-lg px-4"
            >
              <Check class="h-3 w-3" />
              Voucher Redeemed
            </div>
          </div>
          <Button
            v-else
            class="bg-teal-600 hover:bg-teal-700 w-full ml-auto disabled:bg-grey-light-400"
            :disabled="!isPlacedOrPartial"
            @click="showPaymentModal = true"
            >Add Savings</Button
          >
        </div>
      </template>

      <template #desktop-actions>
        <Button
          v-if="isThresholdReached && !isCancelled && !isCancelling && !isRedeemed"
          variant="outline"
          class="border border-brand-300 text-brand-700"
          @click="handleRedeem"
          :disabled="isCancelled || isCompleted || isCancelling"
          >Redeem</Button
        >
        <Button
          v-if="!isCancelled && !isCancelling && !isRedeemed"
          variant="outline"
          class="border-error-200 text-error-700 hover:text-error-700"
          @click="cancelOrder"
          >Cancel</Button
        >
        <Button
          v-if="!isRedeemed"
          class="bg-teal-600 hover:bg-teal-700 disabled:bg-grey-light-400"
          @click="showPaymentModal = true"
          :disabled="!isPlacedOrPartial"
          >Add Savings</Button
        >
        <div
          v-else
          class="flex items-center justify-center gap-2 border-[1px] border-boost-500 text-boost-500 bg-boost-25 h-[40px] rounded-lg px-4"
        >
          <Check class="h-3 w-3" />
          Voucher Redeemed
        </div>
      </template>
    </OrderDetailsCard>

    <StatementCard v-if="!isLoading" :payments="payments" :order="order" />
  </div>

  <Dialog :open="showPaymentModal" @update:open="showPaymentModal = $event">
    <DialogContent class="max-w-[380px] rounded-xl p-3">
      <PaymentModal :order-id="order?._id || ''" />
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import BoostActiveIcon from '@/assets/shopper/active-boost.svg';
import TrophyIcon from '@/assets/shopper/done.svg';
import CustomSkeleton from '@/components/common/CustomSkeleton.vue';
import OrderDetailsCard from '@/components/shopper/OrderDetailsCard.vue';
import PaymentModal from '@/components/shopper/PaymentModal.vue';
import StatementCard from '@/components/shopper/StatementCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import type { IPaymentReport } from '@/models/payments';
import { getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { displayCurrency } from '@/utils/format.utils';
import { paymentReports } from '@/utils/paymentPlan.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import dayjs from 'dayjs';
import { ArrowLeft, Check } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { ModelOrderResponse } from '@layup/api-sdk';

const route = useRoute();
const router = useRouter();
const order = ref<ModelOrderResponse>();
const payments = ref<IPaymentReport[]>([]);
const showPaymentModal = ref(false);

const { isMobile, isTablet } = useBreakpoint();

const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);

const isRedeemed = computed(() => order.value?.voucher?.state === 'CREATED');
const orderBalance = computed(() => order.value?.balance ?? 0);
const orderTotal = computed(() => order.value?.amountDue ?? 0);
const paidAmount = computed(() => orderTotal.value - orderBalance.value);
const isCancelled = computed(() => order.value?.state === 'CANCELLED');
const isCancelling = computed(() => order.value?.state === 'CANCELLING');
const showOverlay = computed(() => isCancelled.value || isCancelling.value);
const isCompleted = computed(() => order.value?.state === 'COMPLETED');
const isRewards = computed(() => order.value?.type === 'REWARDS');
const isPlacedOrPartial = computed(() => order.value?.state === 'PLACED' || order.value?.state === 'PARTIAL');
const isLoading = ref(true);

const progress = computed(() => {
  if (!orderTotal.value) return 0;
  return Math.round((paidAmount.value / orderTotal.value) * 100);
});

const boostRewardAmount = computed(() => {
  const amount =
    order.value?.voucherSettings?.rewardType === 'PERCENTAGE'
      ? (order.value?.voucherSettings.rewardPercentage! / 100) * orderTotal.value
      : order.value?.voucherSettings?.rewardFlatAmountInCents;
  return amount ?? 0;
});

const savingsThreshold = computed(() => order.value?.voucherSettings?.minimumSavingGoalInCents ?? 0);

const duration = computed(() => {
  if (!order.value?.endDateMax) return '0 months';
  const months = dayjs(order.value.endDateMax).diff(dayjs(), 'month');
  return `${months} months`;
});

const remainingTime = computed(() => {
  if (!order.value?.endDateMax) return '0 months remaining';
  const months = Math.max(0, dayjs(order.value.endDateMax).diff(dayjs(), 'month'));
  return `${months} months remaining`;
});

const isThresholdReached = computed(() => paidAmount.value >= savingsThreshold.value);

const valueTextClass = computed(() => (isCancelled.value ? 'text-grey-light-300' : 'text-brand-700'));
const progressTextClass = computed(() => (isCancelled.value ? 'text-grey-light-300' : 'text-brand-600'));

const handleRedeem = () => {
  // TODO: Implement redeem functionality
  console.log('Redeem voucher');
};

const init = async () => {
  try {
    const orderId = route.params.id as string;
    if (orderId) {
      isLoading.value = true;
      order.value = await getOrderByIdWithPlansAndPayments(orderId);
      if (order.value?.plans) {
        payments.value = paymentReports(order.value?.state ?? '', order.value.plans[0]);
      }
    }

    window.scrollTo(0, 0);
  } finally {
    isLoading.value = false;
  }
};

const cancelOrder = () => {
  // TODO: boost order cancellation
};
const goBack = () => {
  router.back();
};

init();
</script>
